import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { taskScanApi } from '@/lib/api/taskScan'

export function boxScanQueryKey(taskUuid: string, boxNum: number) {
  return ['task-scan-box', taskUuid, boxNum] as const
}

export function useBoxScan(
  taskUuid: string | undefined,
  boxNum: number | undefined,
  enabled = true,
  wsConnected = false,
) {
  return useQuery({
    queryKey: boxScanQueryKey(taskUuid ?? '', boxNum ?? 0),
    queryFn: async () => {
      const res = await taskScanApi.getBox(taskUuid!, boxNum!)
      return res.data.item
    },
    enabled: Boolean(taskUuid && boxNum && enabled),
    refetchInterval: q => {
      const box = q.state.data
      if (!box) return wsConnected ? 15_000 : 2000
      if (box.status === 'open') return wsConnected ? 5000 : 2000
      // printed arrives via WS after CompleteJob; poll is fallback only
      if (box.status === 'closed' && !box.printed) return wsConnected ? 5000 : 1500
      return wsConnected ? 15_000 : false
    },
  })
}

export function useBoxScanMutations(taskUuid: string, boxNum: number) {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: boxScanQueryKey(taskUuid, boxNum) })

  const openBox = useMutation({
    mutationFn: (input: { workerName: string; agentId?: string }) =>
      taskScanApi.openBox(taskUuid, boxNum, input.workerName, input.agentId),
    onSuccess: invalidate,
  })

  const scan = useMutation({
    mutationFn: ({ code, workerName, agentId }: { code: string; workerName: string; agentId?: string }) =>
      taskScanApi.scan(taskUuid, boxNum, code, workerName, agentId),
    onSuccess: invalidate,
  })

  const closeBox = useMutation({
    mutationFn: () => taskScanApi.closeBox(taskUuid, boxNum),
    onSuccess: invalidate,
  })

  const reopenBox = useMutation({
    mutationFn: (input: { workerName: string; agentId?: string }) =>
      taskScanApi.reopenBox(taskUuid, boxNum, input.workerName, input.agentId),
    onSuccess: invalidate,
  })

  return { openBox, scan, closeBox, reopenBox }
}
